// client/src/components/pages/LexiStar/Reports/reportAtoms.tsx
//
// Shared pieces of the LexiStar report documents (ST-00188 M4-R2): the print
// mode switch, the render-readiness frame the renderer waits on, the paper
// document shell (masthead, title, facts, AMENDED watermark, footer), sections,
// definition rows, provenance marks, "how was this calculated?" workings, the
// export buttons and a typed view of the auth context.

import React, { createContext, useContext, useEffect, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { fmtDateTime } from '../StatusBoard/format';
import {
  errorText,
  resolveDownload,
  runReport,
  startDownload,
  waitForRun,
} from '../../../../services/lexistarReports';
import { FORMAT_LABELS } from './reportLogic';
import type { ReportFormat, ReportRun } from '../../../../types/lexistar-reports';
import './report.css';

// ── Print mode ──────────────────────────────────────────────────────────────

/** True inside the renderer's chrome-free shell (ReportRenderShell). */
export const ReportRenderContext = createContext(false);

/** The A4 print layout: `?print=1`, or always inside the renderer. */
export function usePrintMode(): boolean {
  const [params] = useSearchParams();
  const rendering = useContext(ReportRenderContext);
  return rendering || params.get('print') === '1';
}

/** True while the browser prints (so closed workings open for the paper). */
export function usePrinting(): boolean {
  const [printing, setPrinting] = useState(false);
  useEffect(() => {
    // flushSync: the browser lays the page out right after beforeprint returns.
    const on = () => flushSync(() => setPrinting(true));
    const off = () => setPrinting(false);
    window.addEventListener('beforeprint', on);
    window.addEventListener('afterprint', off);
    return () => {
      window.removeEventListener('beforeprint', on);
      window.removeEventListener('afterprint', off);
    };
  }, []);
  return printing;
}

export const PrintContext = createContext(false);

/** Where a printed document collects its workings (ReportDoc's appendix). */
const WorkingsTarget = createContext<HTMLElement | null>(null);
/** The title of the section a "how was this calculated?" block belongs to. */
const SectionTitle = createContext<string>('');

// ── Frame: readiness for the renderer ───────────────────────────────────────

export type LoadState = 'loading' | 'ready' | 'error';

/**
 * Wraps a report page. The renderer (lexistar-renderer, M4-R1) prints once
 * `[data-report-ready="true"]` is in the DOM and gives up on
 * `[data-report-error]`; `.lxr-print-root` scopes the print stylesheet.
 */
export function ReportFrame({
  print,
  state,
  error,
  toolbar,
  children,
}: {
  print: boolean;
  state: LoadState;
  error?: string | null;
  toolbar?: React.ReactNode;
  children: React.ReactNode;
}) {
  const printing = usePrinting();
  return (
    <PrintContext.Provider value={print || printing}>
      <div className={`min-h-screen ${print ? 'bg-slate-200 py-4 sm:py-8' : 'bg-slate-100 py-4 sm:py-6'} px-3 sm:px-6`}>
        {toolbar && (
          <div
            className={`lxr-noprint mx-auto mb-4 flex flex-wrap items-center justify-between gap-2 ${print ? 'max-w-[210mm]' : 'max-w-[1180px]'}`}
          >
            {toolbar}
          </div>
        )}
        <div
          className="lxr-print-root"
          data-testid="report-root"
          data-report-ready={state === 'ready' ? 'true' : 'false'}
          {...(state === 'error' ? { 'data-report-error': error || 'error' } : {})}
        >
          {children}
        </div>
      </div>
    </PrintContext.Provider>
  );
}

// ── Document shell ──────────────────────────────────────────────────────────

function Mark() {
  // The LexiStar four-point star in the brand accent (as the certificate).
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 1.5 L14.2 9.8 L22.5 12 L14.2 14.2 L12 22.5 L9.8 14.2 L1.5 12 L9.8 9.8 Z"
        fill="#45b7aa"
      />
    </svg>
  );
}

export interface Fact {
  label: string;
  value: React.ReactNode;
  /** Takes two columns (long values such as a truck combination). */
  wide?: boolean;
}

export function ReportDoc({
  print,
  reportName,
  title,
  subtitle,
  chips,
  facts,
  watermark,
  banner,
  generatedAt,
  footer,
  label,
  children,
}: {
  print: boolean;
  reportName: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  chips?: React.ReactNode;
  facts?: Fact[];
  /** Diagonal watermark word (AMENDED) — repeats on every printed page. */
  watermark?: string | null;
  banner?: React.ReactNode;
  generatedAt?: string | null;
  footer?: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  const [workings, setWorkings] = useState<HTMLElement | null>(null);
  return (
    <article className={`lxr-doc ${print ? 'lxr-doc--a4' : 'lxr-doc--screen'}`} aria-label={label}>
      {watermark && (
        <div className="lxr-watermark" aria-hidden="true" data-testid="report-watermark">
          <span>{watermark}</span>
        </div>
      )}
      <div className="lxr-body">
        <header className="lxr-head">
          <div>
            <div className="lxr-wordmark">
              <Mark />
              LexiStar
            </div>
            <div className="lxr-descriptor">trip intelligence for bulk haulage</div>
          </div>
          <div className="lxr-meta">
            <strong>{reportName}</strong>
            Generated {fmtDateTime(generatedAt || new Date().toISOString())} SAST
          </div>
        </header>
        <div className="lxr-title-row">
          <div className="min-w-0">
            <h1 className="lxr-title">{title}</h1>
            {subtitle && <p className="lxr-subtitle">{subtitle}</p>}
          </div>
          {chips && (
            <div className="lxr-chips" data-testid="report-chips">
              {chips}
            </div>
          )}
        </div>
        {banner}
        {facts && facts.length > 0 && (
          <dl className="lxr-facts" data-testid="report-facts">
            {facts.map((f) => (
              <div key={f.label} className={f.wide ? 'lxr-fact--wide' : undefined}>
                <dt>{f.label}</dt>
                <dd>{f.value}</dd>
              </div>
            ))}
          </dl>
        )}
        <WorkingsTarget.Provider value={print ? workings : null}>{children}</WorkingsTarget.Provider>
        {/* On paper the workings cannot be expanded: they are collected here, one block per section. */}
        {print && (
          <section className="lxr-section lxr-workings" aria-label="How the figures were calculated">
            <h2>
              <span>How the figures were calculated</span>
            </h2>
            <div ref={setWorkings} className="lxr-workings-body" />
          </section>
        )}
        <footer className="lxr-foot">{footer}</footer>
      </div>
    </article>
  );
}

export function Section({
  title,
  aside,
  wide,
  className = '',
  children,
}: {
  title: string;
  aside?: React.ReactNode;
  wide?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section className={`lxr-section ${wide ? 'lxr-section--wide' : ''} ${className}`} aria-label={title}>
      <h2>
        <span>{title}</span>
        {aside && <small>{aside}</small>}
      </h2>
      <SectionTitle.Provider value={title}>{children}</SectionTitle.Provider>
    </section>
  );
}

export function Row({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

// ── Provenance (LS-09 §3: ● Measured ◐ Derived ○ Estimated) ─────────────────

const PROVENANCE: Record<string, { glyph: string; label: string; help: string }> = {
  measured: { glyph: '●', label: 'Measured', help: 'Measured — from the vehicle (CAN, fuel probe, odometer) or the weighbridge.' },
  derived: { glyph: '◐', label: 'Derived', help: 'Derived — calculated from GPS positions or fuel-level changes.' },
  estimated: { glyph: '○', label: 'Estimated', help: 'Estimated — from norms, rate cards and prices.' },
  mixed: { glyph: '◑', label: 'Mixed', help: 'Mixed — some inputs measured, others derived or estimated.' },
};

export function normaliseProvenance(p: unknown): string | null {
  if (!p) return null;
  const v = String(p).toLowerCase();
  if (v in PROVENANCE) return v;
  if (['can', 'probe', 'odometer', 'can_total_fuel', 'weighbridge'].includes(v)) return 'measured';
  if (['gps', 'level_delta', 'level', 'measured_or_derived'].includes(v)) return 'derived';
  if (['norm', 'estimate', 'budget', 'planned'].includes(v)) return 'estimated';
  return null;
}

export function Prov({ provenance }: { provenance: unknown }) {
  const p = normaliseProvenance(provenance);
  if (!p) return null;
  const meta = PROVENANCE[p]!;
  return (
    <span className="lxr-prov" title={meta.help} aria-label={meta.label} role="img">
      {meta.glyph}
    </span>
  );
}

export function ProvenanceKey() {
  return (
    <span>
      ● measured · ◐ derived · ○ estimated
    </span>
  );
}

// ── Workings ────────────────────────────────────────────────────────────────

/**
 * "How was this calculated?" — an expander on screen; on the printed document
 * (print layout) the same workings go to the appendix under the section's name;
 * printing the screen view opens them in place.
 */
export function How({
  children,
  label = 'How was this calculated?',
}: {
  children: React.ReactNode;
  label?: string;
}) {
  const printing = useContext(PrintContext);
  const target = useContext(WorkingsTarget);
  const section = useContext(SectionTitle);
  const [open, setOpen] = useState(false);
  if (target) {
    return createPortal(
      <div className="lxr-working" role="group" aria-label={`Workings: ${section}`}>
        <h3>{section}</h3>
        <div className="lxr-how-body">{children}</div>
      </div>,
      target
    );
  }
  return (
    <details
      className="lxr-how"
      aria-label="Workings"
      open={printing || open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary>{label}</summary>
      <div className="lxr-how-body">{children}</div>
    </details>
  );
}

// ── Export buttons (run the report, wait, download) ─────────────────────────

const RUN_WORDS: Record<string, string> = {
  queued: 'Queued…',
  rendering: 'Rendering…',
  done: 'Ready',
  failed: 'Failed',
};

export function ExportButtons({
  reportKey,
  parameters,
  formats,
  disabled,
  filenameBase,
  buttonClassName,
  onRun,
}: {
  reportKey: string;
  parameters: Record<string, unknown>;
  formats: ReportFormat[];
  disabled?: boolean;
  filenameBase?: string;
  buttonClassName?: string;
  onRun?: (run: ReportRun) => void;
}) {
  const [busy, setBusy] = useState<ReportFormat | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const go = async (format: ReportFormat) => {
    setBusy(format);
    setError(null);
    setStatus(RUN_WORDS.queued ?? null);
    try {
      const started = await runReport(reportKey, { parameters, formats: [format] });
      onRun?.(started);
      const run = await waitForRun(started.id, {
        onUpdate: (r) => setStatus(RUN_WORDS[r.state] || r.state),
      });
      if (run.state === 'failed') throw new Error(run.error || 'The report could not be produced.');
      const link = await resolveDownload(run.id, format);
      startDownload(link.url, link.filename || (filenameBase ? `${filenameBase}.${format}` : undefined));
      setStatus(`${FORMAT_LABELS[format]} ready — downloading.`);
    } catch (err) {
      setStatus(null);
      setError(errorText(err, (err as Error)?.message || 'The report could not be produced.'));
    } finally {
      setBusy(null);
    }
  };

  const cls =
    buttonClassName ||
    'inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed';
  return (
    <div className="inline-flex flex-wrap items-center gap-2">
      {formats.map((f) => (
        <button
          key={f}
          type="button"
          className={cls}
          disabled={disabled || busy !== null}
          onClick={() => go(f)}
          aria-label={`Download ${FORMAT_LABELS[f]}`}
        >
          {busy === f ? `${FORMAT_LABELS[f]}…` : FORMAT_LABELS[f]}
        </button>
      ))}
      {(status || error) && (
        <span role="status" className={`text-xs ${error ? 'text-red-700' : 'text-slate-600'}`}>
          {error || status}
        </span>
      )}
    </div>
  );
}

// ── Screen-side states ──────────────────────────────────────────────────────

export function PaperMessage({
  print,
  title,
  children,
}: {
  print: boolean;
  title: string;
  children?: React.ReactNode;
}) {
  return (
    <div className={`lxr-doc ${print ? 'lxr-doc--a4' : 'lxr-doc--screen'}`} role="status">
      <div className="lxr-body">
        <h1 className="lxr-title" style={{ fontSize: 20 }}>
          {title}
        </h1>
        {children && <div className="lxr-prose" style={{ marginTop: 8 }}>{children}</div>}
      </div>
    </div>
  );
}

export const toolbarButton =
  'inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50';
export const toolbarPrimary =
  'inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg shadow-sm disabled:opacity-50';
